import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const PLATFORMS = [
  { key: "instagram", label: "INSTAGRAM", zh: "Instagram" },
  { key: "facebook", label: "FACEBOOK", zh: "Facebook" },
  { key: "tiktok", label: "TIKTOK", zh: "TikTok" },
  { key: "youtube", label: "YOUTUBE", zh: "YouTube" },
  { key: "xiaohongshu", label: "XIAOHONGSHU", zh: "小红书" },
  { key: "weibo", label: "WEIBO", zh: "微博" },
];

export default function SocialMatrix({ compact = false }) {
  const { lang } = useLang();
  const zh = lang === "zh";
  const { data: settings = {} } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get("/settings").then((r) => r.data),
    staleTime: 60_000,
  });

  const tiles = PLATFORMS.map((p) => ({
    ...p,
    handle: settings[p.key] || "",
    url: settings[`${p.key}_url`] || "",
  }));

  return (
    <section data-testid="social-matrix" className="py-20 md:py-28 bg-[#050505]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        {!compact && (
          <FadeUp className="border-b border-white/10 pb-6 mb-10 md:mb-16">
            <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-3">FOLLOW THE DREAM</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] text-[#f5f5f5]">{zh ? "关注我们" : "SOCIAL MATRIX"}</h2>
          </FadeUp>
        )}
        <div className={`grid gap-4 ${compact ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-3"}`}>
          {tiles.map((t, i) => {
            const live = !!t.url;
            const inner = (
              <>
                <div className="flex items-start justify-between gap-3 w-full">
                  <span className="font-display text-xl md:text-2xl uppercase text-[#f5f5f5] group-hover:text-[#ffde00] transition-colors duration-300">
                    {zh && t.zh ? t.zh : t.label}
                  </span>
                  {live ? (
                    <ArrowUpRight size={18} className="shrink-0 text-[#ffde00] opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300" />
                  ) : (
                    <span className="font-mono2 text-[8px] text-[#52525b] border border-white/15 px-1.5 py-0.5 shrink-0">
                      {zh ? "即将开通" : "SOON"}
                    </span>
                  )}
                </div>
                <span className={`font-mono2 text-[10px] tracking-[0.2em] mt-2 ${live ? "text-[#a1a1aa] group-hover:text-[#ffde00]" : "text-[#52525b]"} transition-colors duration-300`}>
                  {t.handle || (zh ? "账号整理中" : "COMING SOON")}
                </span>
              </>
            );
            return (
              <FadeUp key={t.key} delay={Math.min(i * 0.05, 0.3)}>
                {live ? (
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`social-${t.key}`}
                    className="group border border-white/10 bg-[#0a0a0a] hover:border-[#ffde00] transition-colors duration-300 p-6 h-full flex flex-col"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    className="group border border-white/10 bg-[#0a0a0a] p-6 h-full flex flex-col opacity-60 cursor-default"
                    data-testid={`social-${t.key}`}
                  >
                    {inner}
                  </div>
                )}
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}
